import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Check, Trash2, Eye, EyeOff, Filter, Search, Calendar, DollarSign, AlertCircle, Info, Star, Users, MapPin, Clock } from 'lucide-react';
import { domain } from './Domain.jsx';
import useApiRequest from './useApiRequest.jsx';
import { useLoginContext } from './LoginContext.jsx';
import notify from './Nots.jsx';
import Header from './Header.jsx';
import Footer from './Footer.jsx';

const Notifications = () => {
    const { getRequest, putRequest, deleteRequest, postRequest } = useApiRequest();
    const { userInfo } = useLoginContext();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, unread, read
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedNotifications, setSelectedNotifications] = useState([]);

    // Fetch notifications
    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const response = await getRequest(`${domain}/notifications`);
            if (response.status === 200) {
                setNotifications(response.body);
            }
        } catch (error) {
            notify('Failed to fetch notifications', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userInfo?.accessToken) {
            fetchNotifications();
        }
    }, [userInfo]);

    // Mark notification as read
    const markAsRead = async (notificationId) => {
        try {
            const response = await putRequest(`${domain}/notifications/${notificationId}/mark-read`);
            if (response.status === 200) {
                setNotifications(prev => 
                    prev.map(notif => 
                        notif.id === notificationId 
                            ? { ...notif, is_read: true }
                            : notif
                    )
                );
                notify('Notification marked as read', 'success');
            }
        } catch (error) {
            notify('Failed to mark notification as read', 'error');
        }
    };

    // Mark all notifications as read
    const markAllAsRead = async () => {
        try {
            const response = await putRequest(`${domain}/notifications/mark-all-read`);
            if (response.status === 200) {
                setNotifications(prev => 
                    prev.map(notif => ({ ...notif, is_read: true }))
                );
                notify('All notifications marked as read', 'success');
            }
        } catch (error) {
            notify('Failed to mark all notifications as read', 'error');
        }
    };

    // Delete notification
    const deleteNotification = async (notificationId) => {
        try {
            const response = await deleteRequest(`${domain}/notifications/${notificationId}/delete`);
            if (response.status === 200) {
                setNotifications(prev => 
                    prev.filter(notif => notif.id !== notificationId)
                );
                notify('Notification deleted', 'success');
            }
        } catch (error) {
            notify('Failed to delete notification', 'error');
        }
    };

    // Delete selected notifications
    const deleteSelected = async () => {
        if (selectedNotifications.length === 0) return;
        
        try {
            const response = await postRequest(`${domain}/notifications/bulk-delete`, {
                notification_ids: selectedNotifications
            });
            
            if (response.status === 200) {
                setNotifications(prev => 
                    prev.filter(notif => !selectedNotifications.includes(notif.id))
                );
                const deletedCount = response.body?.deleted_count || selectedNotifications.length;
                setSelectedNotifications([]);
                notify(`${deletedCount} notification(s) deleted`, 'success');
            }
        } catch (error) {
            notify('Failed to delete selected notifications', 'error');
        }
    };

    // Toggle notification selection
    const toggleSelection = (notificationId) => {
        setSelectedNotifications(prev => 
            prev.includes(notificationId)
                ? prev.filter(id => id !== notificationId)
                : [...prev, notificationId]
        );
    };

    // Toggle select all notifications
    const toggleSelectAll = () => {
        const allFilteredIds = filteredNotifications.map(n => n.id);
        const areAllSelected = allFilteredIds.every(id => selectedNotifications.includes(id));
        
        if (areAllSelected) {
            // Deselect all filtered notifications
            setSelectedNotifications(prev => 
                prev.filter(id => !allFilteredIds.includes(id))
            );
        } else {
            // Select all filtered notifications
            setSelectedNotifications(prev => {
                const newSelection = [...prev];
                allFilteredIds.forEach(id => {
                    if (!newSelection.includes(id)) {
                        newSelection.push(id);
                    }
                });
                return newSelection;
            });
        }
    };

    // Get notification icon based on type
    const getNotificationIcon = (type) => {
        const iconMap = {
            'event_created': <Calendar className="w-5 h-5" />,
            'ticket_purchased': <DollarSign className="w-5 h-5" />,
            'payment_received': <DollarSign className="w-5 h-5" />,
            'event_updated': <Info className="w-5 h-5" />,
            'event_cancelled': <AlertCircle className="w-5 h-5" />,
            'ticket_cancelled': <AlertCircle className="w-5 h-5" />,
            'refund_processed': <DollarSign className="w-5 h-5" />,
            'system_announcement': <Info className="w-5 h-5" />,
            'event_reminder': <Clock className="w-5 h-5" />,
            'payment_failed': <AlertCircle className="w-5 h-5" />
        };
        return iconMap[type] || <Bell className="w-5 h-5" />;
    };

    // Get notification color based on type
    const getNotificationColor = (type) => {
        const colorMap = {
            'event_created': 'from-green-400 to-green-500',
            'ticket_purchased': 'from-blue-400 to-blue-500',
            'payment_received': 'from-green-400 to-green-500',
            'event_updated': 'from-blue-400 to-blue-500',
            'event_cancelled': 'from-red-400 to-red-500',
            'ticket_cancelled': 'from-red-400 to-red-500',
            'refund_processed': 'from-orange-400 to-orange-500',
            'system_announcement': 'from-purple-400 to-purple-500',
            'event_reminder': 'from-teal-400 to-teal-500',
            'payment_failed': 'from-red-400 to-red-500'
        };
        return colorMap[type] || 'from-gray-400 to-gray-500';
    };

    // Filter notifications
    const filteredNotifications = notifications.filter(notification => {
        const matchesFilter = filter === 'all' || 
            (filter === 'unread' && !notification.is_read) ||
            (filter === 'read' && notification.is_read);
        
        const matchesSearch = notification.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            notification.message.toLowerCase().includes(searchQuery.toLowerCase());
        
        return matchesFilter && matchesSearch;
    });

    // Check if all filtered notifications are selected
    const allSelected = filteredNotifications.length > 0 && 
        filteredNotifications.every(n => selectedNotifications.includes(n.id));

    if (!userInfo?.accessToken) {
        return (
            <div className="min-h-screen bg-gray-50">
                <Header />
                <div className="container mx-auto px-4 py-12 md:py-16">
                    <div className="text-center">
                        <Bell className="w-12 h-12 md:w-16 md:h-16 text-gray-400 mx-auto mb-3 md:mb-4" />
                        <h2 className="text-lg md:text-2xl font-bold text-gray-900 mb-2">Please Log In</h2>
                        <p className="text-sm md:text-base text-gray-600">You need to be logged in to view your notifications.</p>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Header />
            
            <div className="container mx-auto px-4 py-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 md:mb-6 gap-4">
                        <div>
                            <h1 className="text-lg md:text-3xl font-bold text-gray-900 mb-2">Notifications</h1>
                            <p className="text-sm md:text-base text-gray-600">
                                Stay updated with your latest activities and important updates
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                            {selectedNotifications.length > 0 && (
                                <button
                                    onClick={deleteSelected}
                                    className="px-3 py-2 md:px-4 md:py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors duration-200 flex items-center gap-2 text-sm"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    <span className="hidden sm:inline">Delete Selected</span>
                                    <span className="sm:hidden">Delete ({selectedNotifications.length})</span>
                                </button>
                            )}
                            <button
                                onClick={markAllAsRead}
                                className="px-3 py-2 md:px-4 md:py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg transition-colors duration-200 flex items-center gap-2 text-sm"
                            >
                                <Check className="w-4 h-4" />
                                <span className="hidden sm:inline">Mark All Read</span>
                                <span className="sm:hidden">Mark Read</span>
                            </button>
                        </div>
                    </div>

                    {/* Filters and Search */}
                    <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-4 md:mb-6">
                        <div className="flex-1">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search notifications..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-9 md:pl-10 pr-3 md:pr-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 text-sm"
                                />
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <select
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                className="px-3 py-2 md:px-4 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 text-sm"
                            >
                                <option value="all">All Notifications</option>
                                <option value="unread">Unread Only</option>
                                <option value="read">Read Only</option>
                            </select>
                        </div>
                    </div>
                </motion.div>

                {/* Notifications List */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    {loading ? (
                        <div className="p-6 md:p-8 text-center">
                            <div className="animate-spin rounded-full h-6 w-6 md:h-8 md:w-8 border-b-2 border-orange-500 mx-auto"></div>
                            <p className="text-sm md:text-base text-gray-600 mt-2">Loading notifications...</p>
                        </div>
                    ) : filteredNotifications.length === 0 ? (
                        <div className="p-6 md:p-8 text-center">
                            <Bell className="w-12 h-12 md:w-16 md:h-16 text-gray-400 mx-auto mb-3 md:mb-4" />
                            <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">No notifications</h3>
                            <p className="text-sm md:text-base text-gray-600">
                                {searchQuery || filter !== 'all' 
                                    ? 'No notifications match your current filters.'
                                    : 'You\'re all caught up! No new notifications.'
                                }
                            </p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-200">
                            {/* Select All Header */}
                            <div className="p-4 md:p-6 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        onChange={toggleSelectAll}
                                        className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                                    />
                                    <label className="text-sm md:text-base font-medium text-gray-700 cursor-pointer" onClick={toggleSelectAll}>
                                        Select All ({filteredNotifications.length})
                                    </label>
                                </div>
                                {selectedNotifications.length > 0 && (
                                    <button
                                        onClick={deleteSelected}
                                        className="px-3 py-1.5 md:px-4 md:py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors duration-200 flex items-center gap-2 text-sm"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        <span>Delete Selected ({selectedNotifications.length})</span>
                                    </button>
                                )}
                            </div>
                            {filteredNotifications.map((notification, index) => (
                                <motion.div
                                    key={notification.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className={`p-4 md:p-6 hover:bg-gray-50 transition-colors duration-200 ${
                                        !notification.is_read ? 'bg-orange-50' : ''
                                    }`}
                                >
                                    <div className="flex items-start gap-3 md:gap-4">
                                        {/* Checkbox */}
                                        <input
                                            type="checkbox"
                                            checked={selectedNotifications.includes(notification.id)}
                                            onChange={() => toggleSelection(notification.id)}
                                            className="mt-1 w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                                        />
                                        
                                        {/* Icon */}
                                        <div className={`w-8 h-8 md:w-10 md:h-10 bg-gradient-to-r ${getNotificationColor(notification.notification_type)} rounded-lg flex items-center justify-center text-white flex-shrink-0`}>
                                            {getNotificationIcon(notification.notification_type)}
                                        </div>
                                        
                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                                                <div className="flex-1 min-w-0">
                                                    <h3 className={`text-sm md:text-lg font-semibold mb-1 ${
                                                        !notification.is_read ? 'text-gray-900' : 'text-gray-700'
                                                    }`}>
                                                        {notification.title}
                                                    </h3>
                                                    <p className="text-xs md:text-base text-gray-600 mb-2">
                                                        {notification.message}
                                                    </p>
                                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs md:text-sm text-gray-500">
                                                        <span>{notification.time_ago}</span>
                                                        {notification.event_title && (
                                                            <span className="flex items-center gap-1">
                                                                <MapPin className="w-3 h-3 md:w-4 md:h-4" />
                                                                <span className="truncate">{notification.event_title}</span>
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                {/* Actions */}
                                                <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                                                    {!notification.is_read && (
                                                        <button
                                                            onClick={() => markAsRead(notification.id)}
                                                            className="p-1.5 md:p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors duration-200"
                                                            title="Mark as read"
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => deleteNotification(notification.id)}
                                                        className="p-1.5 md:p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                                                        title="Delete notification"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Stats */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mt-6 md:mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6"
                >
                    <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center gap-2 md:gap-3">
                            <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg flex items-center justify-center">
                                <Bell className="w-4 h-4 md:w-5 md:h-5 text-white" />
                            </div>
                            <div>
                                <p className="text-xs md:text-sm text-gray-600">Total Notifications</p>
                                <p className="text-lg md:text-2xl font-bold text-gray-900">{notifications.length}</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center gap-2 md:gap-3">
                            <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-r from-red-400 to-red-500 rounded-lg flex items-center justify-center">
                                <EyeOff className="w-4 h-4 md:w-5 md:h-5 text-white" />
                            </div>
                            <div>
                                <p className="text-xs md:text-sm text-gray-600">Unread</p>
                                <p className="text-lg md:text-2xl font-bold text-gray-900">
                                    {notifications.filter(n => !n.is_read).length}
                                </p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center gap-2 md:gap-3">
                            <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-r from-teal-400 to-teal-500 rounded-lg flex items-center justify-center">
                                <Eye className="w-4 h-4 md:w-5 md:h-5 text-white" />
                            </div>
                            <div>
                                <p className="text-xs md:text-sm text-gray-600">Read</p>
                                <p className="text-lg md:text-2xl font-bold text-gray-900">
                                    {notifications.filter(n => n.is_read).length}
                                </p>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
            
            <Footer />
        </div>
    );
};

export default Notifications; 